import type { components } from "@/shared/api";

import { inAppBrowser } from "./inAppBrowser";
import { isSteamLoginReturn } from "./steamLogin";

export type Landing = components["schemas"]["Landing"];

const STORAGE_KEY = "relock.landing";
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign"] as const;

// Домен сайта, с которого пришли: только чужой сайт и только домен, без пути и параметров.
// Возврат со входа Steam — не источник, а наш же вход.
const externalReferrer = (referrer: string, origin: string): string | undefined => {
  if (!referrer) {
    return undefined;
  }
  try {
    const url = new URL(referrer);
    if (url.origin === origin || isSteamLoginReturn(url)) {
      return undefined;
    }
    return url.hostname;
  } catch {
    return undefined;
  }
};

// Запомнить первый заход на сайт: когда, откуда (домен), с какими метками ссылки и в каком
// приложении (встроенный браузер TikTok, Instagram…). Уже запомнен — не трогает: источник —
// первый заход, а не последний. Хранилище недоступно (приватный режим, запрет) — источник
// останется неизвестным, сайт работает.
export const captureLanding = (
  location: Location = window.location,
  referrer: string = document.referrer,
  storage: Storage = window.localStorage,
  now: Date = new Date(),
  userAgent: string = window.navigator.userAgent,
): void => {
  try {
    if (storage.getItem(STORAGE_KEY) !== null) {
      return;
    }
    const url = new URL(location.href);
    const landing: Landing = {
      landed_at: now.toISOString(),
      referrer: externalReferrer(referrer, url.origin) ?? null,
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      landing_path: url.pathname,
      in_app: inAppBrowser(userAgent),
    };
    for (const param of UTM_PARAMS) {
      landing[param] = url.searchParams.get(param)?.slice(0, 100) || null;
    }
    storage.setItem(STORAGE_KEY, JSON.stringify(landing));
  } catch {
    // Без хранилища — без источника.
  }
};

// Запомненный первый заход или `null`.
export const readLanding = (storage: Storage = window.localStorage): Landing | null => {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    const value: unknown = JSON.parse(raw);
    if (typeof value === "object" && value !== null && "landed_at" in value) {
      return value as Landing;
    }
    return null;
  } catch {
    return null;
  }
};

// Забыть первый заход: отправлен или больше не нужен (аккаунт не новый).
export const forgetLanding = (storage: Storage = window.localStorage): void => {
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // Нечего забывать.
  }
};
